import { createElement, lazy, type ComponentType } from 'react'

/**
 * Route-level code splitting that hydrates cleanly against prerendered HTML.
 *
 * The problem this solves: plain `React.lazy(loader)` cannot hydrate its
 * first render synchronously, no matter how early `loader()` is called
 * beforehand. `React.lazy` calls its OWN `loader()` the first time the
 * component is actually rendered, producing a fresh promise chain — and a
 * Promise's `.then()` callback is *always* deferred to a microtask, even for
 * an already-settled promise. So `hydrateRoot`'s first synchronous render
 * pass always suspends on a lazy route, wants to paint the Suspense
 * fallback, and that doesn't match the real content already in the DOM from
 * SSR — React error #418, confirmed live via Lighthouse's errors-in-console
 * audit against every prerendered route below the homepage, even after
 * pre-calling the same `import()` ahead of `hydrateRoot`.
 *
 * The actual fix: `routeComponent(loader)` returns a component that checks
 * a synchronous cache first. main.tsx resolves (awaits) the CURRENT page's
 * loader and populates that cache BEFORE calling hydrateRoot, so the first
 * render finds the real component already there and renders it directly —
 * no Suspense, no mismatch. Every other route's cache entry stays empty
 * until a real navigation reaches it, at which point it falls back to
 * normal `lazy()` behavior — pure client-side rendering with no SSR content
 * to reconcile against, so a Suspense fallback there is correct, not a bug.
 */

type Loader = () => Promise<{ default: ComponentType<any> }>

const resolved = new Map<Loader, ComponentType<any>>()

export const routeComponent = (loader: Loader): ComponentType<any> => {
  const Lazy = lazy(loader)
  const Wrapped = (props: object) => createElement(resolved.get(loader) ?? Lazy, props)
  return Wrapped
}

/**
 * One loader per distinct rendered component, exported so App.tsx (which
 * builds the route elements) and the PATH_LOADERS table below (which
 * main.tsx consults by URL) reference the exact same function — the cache
 * above is keyed by function identity, not by name, so these must be the
 * same object in both places, not two separately-written copies.
 */
export const loadBuilderPage = () => import('@/pages/Builder')
export const loadToolsIndexPage = () => import('@/pages/Tools').then((m) => ({ default: m.ToolsIndexPage }))
export const loadAtsCheckerPage = () => import('@/pages/Tools').then((m) => ({ default: m.AtsCheckerPage }))
export const loadJobMatcherPage = () => import('@/pages/Tools').then((m) => ({ default: m.JobMatcherPage }))
export const loadBulletImproverPage = () =>
  import('@/pages/Tools').then((m) => ({ default: m.BulletImproverPage }))
export const loadAboutPage = () => import('@/pages/Tools').then((m) => ({ default: m.AboutPage }))
export const loadPrivacyPage = () => import('@/pages/Tools').then((m) => ({ default: m.PrivacyPage }))
export const loadMethodologyPage = () => import('@/pages/Methodology')
export const loadEvidencePage = () => import('@/pages/Evidence')
export const loadGuidesIndex = () => import('@/pages/Landing').then((m) => ({ default: m.GuidesIndex }))
export const loadLandingPageView = () =>
  import('@/pages/Landing').then((m) => ({ default: m.LandingPageView }))
export const loadContactPage = () => import('@/pages/Contact')
export const loadFaqPage = () => import('@/pages/Faq')
export const loadHowItWorksPage = () => import('@/pages/HowItWorks')
export const loadTermsPage = () => import('@/pages/Terms')
export const loadDisclaimerPage = () => import('@/pages/Disclaimer')
export const loadAuthorPage = () => import('@/pages/Author')

/**
 * `/guides/:slug` renders LandingPageView with a `prefix` prop —
 * routeComponent's cache is keyed on the loader alone (props aren't part of
 * the key), which is fine because the cached value is the component, not a
 * pre-rendered element.
 */
const PATH_LOADERS: { test: (pathname: string) => boolean; loader: Loader }[] = [
  { test: (p) => p === '/resume-builder', loader: loadBuilderPage },
  {
    test: (p) =>
      ['/tools', '/ats-resume-checker', '/job-description-matcher', '/resume-bullet-improver', '/privacy', '/about'].includes(
        p,
      ),
    loader: loadToolsIndexPage,
  },
  { test: (p) => p === '/methodology', loader: loadMethodologyPage },
  { test: (p) => p === '/evidence', loader: loadEvidencePage },
  {
    test: (p) => p === '/guides' || p.startsWith('/guides/'),
    loader: loadLandingPageView,
  },
  { test: (p) => p === '/contact', loader: loadContactPage },
  { test: (p) => p === '/faq', loader: loadFaqPage },
  { test: (p) => p === '/how-it-works', loader: loadHowItWorksPage },
  { test: (p) => p === '/terms', loader: loadTermsPage },
  { test: (p) => p === '/disclaimer', loader: loadDisclaimerPage },
  { test: (p) => p.startsWith('/author/'), loader: loadAuthorPage },
]

/**
 * Resolves and caches the component for whichever loader matches `pathname`
 * — called once, in main.tsx, before the first hydrateRoot(). The index
 * pages (/tools, /guides) are matched
 * above via their *named* loaders (loadToolsIndexPage /
 * loadGuidesIndex are separate exports from the same module,
 * per the "keyed by function identity" note above), not the shared
 * loadLandingPageView / loadToolsIndexPage — see the explicit index-path
 * checks before the prefix checks.
 */
export const warmRouteChunk = async (pathname: string): Promise<void> => {
  let loader: Loader | undefined
  if (pathname === '/guides') loader = loadGuidesIndex
  else if (pathname === '/tools') loader = loadToolsIndexPage
  else loader = PATH_LOADERS.find((w) => w.test(pathname))?.loader

  if (!loader) return
  try {
    const mod = await loader()
    resolved.set(loader, mod.default)
  } catch {
    // Chunk failed to load (offline, stale deploy). Leave the cache empty —
    // the route falls back to normal lazy()/Suspense/ChunkErrorBoundary
    // behavior, same as if this warmup step didn't exist.
  }
}
